import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CelebrationOutlinedIcon from "@mui/icons-material/CelebrationOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import InboxIcon from "@mui/icons-material/Inbox";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import PublicIcon from "@mui/icons-material/Public";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import DrawerComponentView from "src/app/components/drawer/drawer.view";
import DrawerAwareContainerComponentView from "src/app/components/drawerAwareContainer/drawerAwareContainer.view";
import SnackBarComponentView from "src/app/components/snackbar/snackbar.view";
import {
  EventInvitation,
  EventJoinRequest,
  EventSummary,
  PublicEventSummary,
} from "src/app/models/screens/events.model";
import { RequestManagementUser } from "src/app/models/screens/requestManagement.model";
import EventsScreenViewModel from "src/app/screens/events/events.viewmodel";
import styles from "src/app/styles/screens/events.style";
import { getEventBannerUrl } from "src/app/utils/backendEndpointCalls.util";

const EventBanner: React.FC<{ event: EventSummary }> = ({ event }) => {
  if (!event.hasBanner) {
    return (
      <Box sx={styles.eventCardBannerFallback}>
        <CelebrationOutlinedIcon fontSize="inherit" />
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src={getEventBannerUrl(event.id)}
      alt={event.title}
      sx={styles.eventCardBanner}
    />
  );
};

const EventCard: React.FC<{
  event: EventSummary;
  onOpen: () => void;
  minutesToLabel: (m: number) => string;
  formatCurrency: (amount: number, currency: string) => string;
  rightSlot?: React.ReactNode;
  showCreator?: boolean;
}> = ({
  event,
  onOpen,
  minutesToLabel,
  formatCurrency,
  rightSlot,
  showCreator,
}) => (
  <Paper elevation={0} sx={styles.eventCard} onClick={onOpen}>
    <EventBanner event={event} />
    <Box sx={styles.eventCardBody}>
      <Box sx={styles.eventCardTitleRow}>
        <Typography sx={styles.eventCardTitle}>{event.title}</Typography>
      </Box>
      <Box sx={styles.eventCardChipsRow}>
        <Chip
          size="small"
          icon={event.isPublic ? <PublicIcon /> : <LockOutlinedIcon />}
          label={event.isPublic ? "Public" : "Private"}
          variant="outlined"
        />
        {event.isPaid && (
          <Chip
            size="small"
            icon={<PaymentsOutlinedIcon />}
            label={formatCurrency(event.participationFee, event.currency)}
            color="warning"
            variant="outlined"
          />
        )}
        {event.isCancelled && (
          <Chip size="small" label="Cancelled" color="error" />
        )}
      </Box>
      <Typography sx={styles.eventCardMeta}>
        {event.date} · {minutesToLabel(event.from)} – {minutesToLabel(event.to)}
      </Typography>
      <Typography sx={styles.eventCardMeta}>
        {event.location || "No location"}
      </Typography>
      {event.description && (
        <Typography sx={styles.eventCardDescription}>
          {event.description}
        </Typography>
      )}
      {showCreator && (
        <Typography sx={styles.eventCardCreator}>
          By {event.creatorFullName}
        </Typography>
      )}
      {rightSlot && <Box sx={styles.eventCardActionsRow}>{rightSlot}</Box>}
    </Box>
  </Paper>
);

const EventsScreenView: React.FC = () => {
  const vm = EventsScreenViewModel();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const goToEvent = (eventId: string) => {
    navigate(`/events/${eventId}`);
  };

  const showOwnerSelector = vm.userRole === "Parent";

  return (
    <Box sx={styles.parent}>
      <DrawerComponentView selectedId="Events" />
      <DrawerAwareContainerComponentView
        drawerWidth={250}
        sx={styles.drawerAwareContainer}
      >
        <Grid container justifyContent="center" spacing={3}>
          <Grid
            size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}
            sx={styles.titleGrid}
          >
            <Typography component="span" sx={styles.titleText}>
              Events
            </Typography>
          </Grid>
          {showOwnerSelector && (
            <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
              <Grid
                size={{ xl: 4, lg: 5, md: 5, sm: 6, xs: 7 }}
                sx={styles.selectorContainer}
                justifySelf="flex-end"
              >
                <Paper elevation={0} sx={styles.selectorPaper}>
                  <FormControl fullWidth>
                    <InputLabel id="event-owner-select-label">
                      {vm.userRole === "Parent"
                        ? "Event Owner"
                        : "Viewing Events Of"}
                    </InputLabel>
                    <Select
                      labelId="event-owner-select-label"
                      value={vm.selectedOwnerId}
                      label={
                        vm.userRole === "Parent"
                          ? "Event Owner"
                          : "Viewing Events Of"
                      }
                      onChange={(event: SelectChangeEvent) =>
                        vm.onOwnerChanged(event.target.value)
                      }
                      disabled={
                        vm.userRole === "Parent"
                          ? vm.isLoadingChildren
                          : vm.isLoadingAdminUsers
                      }
                    >
                      <MenuItem value="you">You</MenuItem>
                      {vm.userRole === "Parent" &&
                        vm.children.map((child) => (
                          <MenuItem key={child._id} value={child._id}>
                            {child.firstName}
                          </MenuItem>
                        ))}
                      {vm.userRole === "Admin" &&
                        vm.adminUsers.map((user: RequestManagementUser) => (
                          <MenuItem key={user.id} value={user.id}>
                            {`${user.firstName} ${user.lastName}`}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            </Grid>
          )}
          <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
            <Paper elevation={0} sx={styles.tabsContainer}>
              <Tabs
                value={vm.selectedTab}
                onChange={vm.onTabChanged}
                variant="fullWidth"
                scrollButtons="auto"
              >
                <Tab
                  icon={<CelebrationOutlinedIcon />}
                  iconPosition="start"
                  label="My Events"
                />
                <Tab
                  icon={<InboxIcon />}
                  iconPosition="start"
                  label="Invitations"
                />
                <Tab
                  icon={<PublicIcon />}
                  iconPosition="start"
                  label="Browse Public"
                />
                <Tab
                  icon={<GroupAddIcon />}
                  iconPosition="start"
                  label="Join Requests"
                />
                <Tab
                  icon={<EventAvailableIcon />}
                  iconPosition="start"
                  label="Create Event"
                />
              </Tabs>
              <Divider />
              {vm.selectedTab === 0 && (
                <Box sx={styles.selectedTab}>
                  <Typography sx={styles.selectedTabText}>My Events</Typography>
                  {vm.isLoadingMyEvents ? (
                    <Box sx={styles.searchingLoader}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : vm.myEvents.length === 0 ? (
                    <Typography sx={styles.noItemsText}>
                      You have no events yet.
                    </Typography>
                  ) : (
                    <Grid container spacing={2}>
                      {vm.myEvents.map((event: EventSummary) => (
                        <Grid key={event.id} size={{ xs: 12, sm: 6, lg: 4 }}>
                          <EventCard
                            event={event}
                            onOpen={() => goToEvent(event.id)}
                            minutesToLabel={vm.minutesToLabel}
                            formatCurrency={vm.formatCurrency}
                            showCreator
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              )}
              {vm.selectedTab === 1 && (
                <Box sx={styles.selectedTab}>
                  <Typography sx={styles.selectedTabText}>
                    Invitations Received
                  </Typography>
                  {vm.isLoadingInvitations ? (
                    <Box sx={styles.searchingLoader}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : vm.invitations.length === 0 ? (
                    <Typography sx={styles.noItemsText}>
                      No invitations.
                    </Typography>
                  ) : (
                    <Grid container spacing={2}>
                      {vm.invitations.map((invitation: EventInvitation) => (
                        <Grid
                          key={invitation.id}
                          size={{ xs: 12, sm: 6, lg: 4 }}
                        >
                          <EventCard
                            event={invitation.event}
                            onOpen={() => goToEvent(invitation.event.id)}
                            minutesToLabel={vm.minutesToLabel}
                            formatCurrency={vm.formatCurrency}
                            showCreator
                            rightSlot={
                              <>
                                <Chip
                                  size="small"
                                  label={invitation.status}
                                  color={
                                    invitation.status === "Accepted"
                                      ? "success"
                                      : invitation.status === "Rejected"
                                        ? "error"
                                        : "warning"
                                  }
                                />
                                {invitation.status === "Pending" && (
                                  <>
                                    <Button
                                      variant="contained"
                                      color="success"
                                      size="small"
                                      startIcon={<CheckCircleOutlineIcon />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        vm.onRespondInvitation(
                                          invitation.id,
                                          true,
                                          invitation,
                                        );
                                      }}
                                      loading={
                                        vm.respondingInvitationId ===
                                        invitation.id
                                      }
                                    >
                                      Accept
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      color="error"
                                      size="small"
                                      startIcon={<CloseIcon />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        vm.onRespondInvitation(
                                          invitation.id,
                                          false,
                                        );
                                      }}
                                      loading={
                                        vm.respondingInvitationId ===
                                        invitation.id
                                      }
                                    >
                                      Reject
                                    </Button>
                                  </>
                                )}
                              </>
                            }
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              )}
              {vm.selectedTab === 2 && (
                <Box sx={styles.selectedTab}>
                  <Typography sx={styles.selectedTabText}>
                    Browse Public Events
                  </Typography>
                  {vm.isLoadingPublicEvents ? (
                    <Box sx={styles.searchingLoader}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : vm.publicEvents.length === 0 ? (
                    <Typography sx={styles.noItemsText}>
                      No upcoming public events.
                    </Typography>
                  ) : (
                    <Grid container spacing={2}>
                      {vm.publicEvents.map((event: PublicEventSummary) => (
                        <Grid key={event.id} size={{ xs: 12, sm: 6, lg: 4 }}>
                          <EventCard
                            event={event}
                            onOpen={() => goToEvent(event.id)}
                            minutesToLabel={vm.minutesToLabel}
                            formatCurrency={vm.formatCurrency}
                            showCreator
                            rightSlot={
                              event.hasPendingJoinRequest ? (
                                <Chip
                                  size="small"
                                  label="Request Pending"
                                  color="warning"
                                />
                              ) : (
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<GroupAddIcon />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    vm.onRequestJoinPublicEvent(
                                      event.id,
                                      event,
                                    );
                                  }}
                                  loading={
                                    vm.requestingJoinEventId === event.id
                                  }
                                >
                                  Request to Join
                                </Button>
                              )
                            }
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              )}
              {vm.selectedTab === 3 && (
                <Box sx={styles.selectedTab}>
                  <Typography sx={styles.selectedTabText}>
                    Join Requests Received
                  </Typography>
                  {vm.isLoadingJoinRequests ? (
                    <Box sx={styles.searchingLoader}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : vm.joinRequests.length === 0 ? (
                    <Typography sx={styles.noItemsText}>
                      No join requests.
                    </Typography>
                  ) : (
                    <Grid container spacing={2}>
                      {vm.joinRequests.map((request: EventJoinRequest) => (
                        <Grid key={request.id} size={{ xs: 12, md: 6 }}>
                          <Paper
                            elevation={0}
                            sx={styles.eventCard}
                            onClick={() => goToEvent(request.event.id)}
                          >
                            <Box sx={styles.eventCardBody}>
                              <Box sx={styles.eventCardTitleRow}>
                                <Typography sx={styles.eventCardTitle}>
                                  {request.requesterFullName}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={request.status}
                                  color={
                                    request.status === "Accepted"
                                      ? "success"
                                      : request.status === "Rejected"
                                        ? "error"
                                        : "warning"
                                  }
                                />
                              </Box>
                              <Typography sx={styles.eventCardMeta}>
                                Wants to join: {request.event.title}
                              </Typography>
                              <Typography sx={styles.eventCardMeta}>
                                {request.event.date} ·{" "}
                                {vm.minutesToLabel(request.event.from)} –{" "}
                                {vm.minutesToLabel(request.event.to)}
                              </Typography>
                              {request.companions.length > 0 && (
                                <Typography sx={styles.eventCardMeta}>
                                  {request.requesterRole === "Model"
                                    ? "Coming with parents: "
                                    : request.requesterRole === "Parent"
                                      ? "Bringing children: "
                                      : "Coming with: "}
                                  {request.companions
                                    .map((c) => c.fullName)
                                    .join(", ")}
                                </Typography>
                              )}
                              {request.status === "Pending" && (
                                <Box sx={styles.requestActionsContainer}>
                                  <Button
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    startIcon={<CheckCircleOutlineIcon />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      vm.onRespondJoinRequest(request.id, true);
                                    }}
                                    loading={
                                      vm.respondingJoinRequestId === request.id
                                    }
                                  >
                                    Accept
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    startIcon={<CloseIcon />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      vm.onRespondJoinRequest(
                                        request.id,
                                        false,
                                      );
                                    }}
                                    loading={
                                      vm.respondingJoinRequestId === request.id
                                    }
                                  >
                                    Reject
                                  </Button>
                                </Box>
                              )}
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              )}
              {vm.selectedTab === 4 && (
                <Box sx={styles.selectedTab}>
                  <Typography sx={styles.selectedTabText}>
                    Create New Event
                  </Typography>
                  <Grid container spacing={3} justifyContent="center">
                    <Grid size={{ xs: 12, md: 8 }}>
                      <Paper elevation={0} sx={styles.composeFormContainer}>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          style={{ display: "none" }}
                          onChange={(e) =>
                            vm.onComposeBannerSelected(
                              e.target.files?.[0] ?? null,
                            )
                          }
                          onClick={(e) =>
                            ((e.target as HTMLInputElement).value = "")
                          }
                        />
                        <Box
                          sx={styles.bannerUploadBox}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          {vm.composeBannerPreviewUrl ? (
                            <>
                              <Box
                                component="img"
                                src={vm.composeBannerPreviewUrl}
                                alt="Banner preview"
                                sx={styles.bannerPreview}
                              />
                              <Box sx={styles.bannerPreviewActions}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    vm.onComposeBannerSelected(null);
                                  }}
                                  sx={{
                                    backgroundColor: "rgba(255,255,255,0.85)",
                                  }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </>
                          ) : (
                            <>
                              <AddPhotoAlternateOutlinedIcon
                                sx={{ fontSize: 40, opacity: 0.5, mb: 1 }}
                              />
                              <Typography sx={{ opacity: 0.7, fontSize: 14 }}>
                                Click to upload a banner image
                              </Typography>
                            </>
                          )}
                        </Box>

                        <TextField
                          fullWidth
                          label="Title"
                          value={vm.composeTitle}
                          onChange={(e) => vm.setComposeTitle(e.target.value)}
                          sx={styles.composeFormField}
                        />

                        <TextField
                          fullWidth
                          multiline
                          minRows={3}
                          label="Description"
                          value={vm.composeDescription}
                          onChange={(e) =>
                            vm.setComposeDescription(e.target.value)
                          }
                          sx={styles.composeFormField}
                        />

                        <TextField
                          fullWidth
                          label="Location (address)"
                          value={vm.composeLocation}
                          onChange={(e) =>
                            vm.setComposeLocation(e.target.value)
                          }
                          sx={styles.composeFormField}
                        />

                        <DatePicker
                          label="Event Date"
                          value={vm.composeDate}
                          onChange={(date) => vm.setComposeDate(date)}
                          minDate={dayjs()}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              sx: styles.composeFormField,
                            },
                          }}
                        />

                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TimePicker
                              label="Start time"
                              value={vm.composeStartTime}
                              onChange={(value) =>
                                vm.setComposeStartTime(value)
                              }
                              ampm={false}
                              minutesStep={5}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  sx: styles.composeFormField,
                                },
                              }}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                              fullWidth
                              label="Duration (minutes)"
                              type="number"
                              inputProps={{ min: 1, step: 5 }}
                              value={vm.composeDurationMinutes}
                              onChange={(e) =>
                                vm.setComposeDurationMinutes(e.target.value)
                              }
                              sx={styles.composeFormField}
                            />
                          </Grid>
                        </Grid>

                        <Box sx={styles.publicSectionBox}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={vm.composeIsPublic}
                                onChange={(e) =>
                                  vm.setComposeIsPublic(e.target.checked)
                                }
                              />
                            }
                            label="Public event (anyone can see and request to join)"
                          />
                        </Box>

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={vm.composeIsPaid}
                              onChange={(e) =>
                                vm.setComposeIsPaid(e.target.checked)
                              }
                            />
                          }
                          label="Paid event (participants are paid for attending)"
                          sx={{ mb: vm.composeIsPaid ? 1 : 2 }}
                        />

                        {vm.composeIsPaid && (
                          <Box sx={styles.paidSectionBox}>
                            <Typography
                              sx={{
                                color: "rgba(0,0,0,0.7)",
                                fontWeight: 600,
                                mb: 1.5,
                                fontSize: 13,
                              }}
                            >
                              Each participant will receive this amount once the
                              event ends:
                            </Typography>
                            <TextField
                              fullWidth
                              label="Participation fee (GBP)"
                              type="number"
                              inputProps={{ min: 0, step: "0.01" }}
                              value={vm.composeFee}
                              onChange={(e) => vm.setComposeFee(e.target.value)}
                              InputProps={{
                                startAdornment: (
                                  <Typography sx={{ mr: 0.5, opacity: 0.6 }}>
                                    £
                                  </Typography>
                                ),
                              }}
                            />
                          </Box>
                        )}

                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<EventAvailableIcon />}
                          onClick={vm.onSubmitComposeEvent}
                          loading={vm.isComposingEvent}
                          disabled={
                            !vm.composeTitle.trim() ||
                            !vm.composeDate ||
                            !vm.composeStartTime ||
                            !vm.composeDurationMinutes ||
                            !vm.composeLocation.trim() ||
                            (vm.composeIsPaid && !vm.composeFee)
                          }
                          sx={styles.submitButton}
                        >
                          Create Event
                        </Button>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DrawerAwareContainerComponentView>

      <Dialog
        open={!!vm.companionPrompt}
        onClose={vm.closeCompanionPrompt}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {vm.companionPrompt?.kind === "invitation"
            ? "Accept Invitation"
            : "Request to Join"}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            {vm.companionPrompt?.kind === "invitation"
              ? `You're accepting "${vm.companionPrompt?.eventTitle}".`
              : `You're requesting to join "${vm.companionPrompt?.eventTitle}".`}
          </Typography>
          <Typography sx={{ mb: 2, opacity: 0.75, fontSize: 13 }}>
            {vm.companionPrompt?.relativeLabel}
            {vm.companionPrompt?.requireAtLeastOne
              ? " — at least one must be selected."
              : " — selection is optional."}
          </Typography>
          {vm.isLoadingCompanionRelatives ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : vm.companionRelatives.length === 0 ? (
            <Typography sx={{ opacity: 0.7, fontSize: 14 }}>
              {vm.companionPrompt?.requireAtLeastOne
                ? "No connected accounts found. You won't be able to proceed without one."
                : "No connected accounts found."}
            </Typography>
          ) : (
            <List sx={{ p: 0 }}>
              {vm.companionRelatives.map((rel) => {
                const checked = vm.selectedCompanionIds.includes(rel.id);
                return (
                  <ListItemButton
                    key={rel.id}
                    onClick={() => vm.toggleCompanionSelection(rel.id)}
                    sx={{ borderRadius: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox edge="start" checked={checked} tabIndex={-1} />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${rel.firstName} ${rel.lastName}`}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={vm.closeCompanionPrompt}
            color="inherit"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={vm.submitCompanionPrompt}
            variant="contained"
            loading={vm.isSubmittingCompanionPrompt}
            disabled={
              vm.companionPrompt?.requireAtLeastOne &&
              vm.selectedCompanionIds.length === 0
            }
          >
            {vm.companionPrompt?.kind === "invitation"
              ? "Accept"
              : "Send Request"}
          </Button>
        </DialogActions>
      </Dialog>

      <SnackBarComponentView
        isError={vm.isSnackbarError}
        isOpen={vm.isDisplayingSnackbar}
        message={vm.snackbarMessage}
        onClose={() => vm.setIsDisplayingSnackbar(false)}
      />
    </Box>
  );
};

export default EventsScreenView;
